import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Form, Formik } from 'formik'
import { Box, Typography } from '@mui/material'
import { useAuth } from '@frontegg/react'
import dayjs from 'dayjs'

/* Utils */
import { getUtcDateAndTime } from 'ui/pages/library/utils'
import { CustomLabelDate, CustomLabelsDataPayload, DateRangeI } from 'models'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'
import axios from 'lib/axios'

/* Components */
import { LabelsShowModeWrapper, CreateLabelsInnerForm, CreateLabelWrapper } from './labelsShowMode.styles'
import { Button, disabledStyles } from 'ui/components/buttons'
import SingleDatePicker from 'ui/components/single-date-picker'
import TextFieldCustomLabel from '../../../settings-drawer/components/TextFieldCustomLabel'
import ControlledTextArea from 'ui/components/form-components/ControlledTextArea'
import FormikRadioGroup from 'ui/components/form-components/FormikRadioGroup'
import { OnyxiaTooltip } from 'ui/components/tooltip'

const visibilityOptions = [
  { name: 'Private', label: 'Private', value: 'private' },
  { name: 'Public', label: 'Public', value: 'public' }
]

interface Props {
  redirectToMode: string
  setLoading: Dispatch<SetStateAction<boolean>>
  customDate: CustomLabelDate
  cpiName: string | undefined
  cpiConfId: string | undefined
  updateMode: (newMode: string) => void
  updateLabels: (payload?: CustomLabelsDataPayload, ignoreCustomPeriod?: boolean) => Promise<void>
  returnToZero: () => void
  dateRange: DateRangeI
  mode: string
  deleteLabelCallback?: (labelId: string) => void
}

const CreateLabels: FC<Props> = ({
  redirectToMode,
  cpiName,
  cpiConfId,
  updateMode,
  updateLabels,
  setLoading,
  customDate,
  returnToZero,
  dateRange,
  mode,
  deleteLabelCallback
}) => {
  const { editLabel } = useLibraryDetails()
  const { user: authUser } = useAuth()

  const initialForm = editLabel?.data
    ? {
        title: editLabel.data.title,
        description: editLabel.data.description,
        cpi_configuration_id: cpiConfId,
        visibility: editLabel.data.is_public ? 'public' : 'private',
        date: editLabel.data.date ? dayjs(editLabel.data.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
      }
    : {
        title: '',
        description: '',
        cpi_configuration_id: cpiConfId,
        visibility: 'private',
        /* The chart range runs to the end of its last stripe, which can be days ahead of today. */
        date: customDate.start
          ? dayjs(customDate.start)
          : customDate.date
          ? dayjs(customDate.date)
          : dateRange && dateRange.maxDate && dayjs(dateRange.maxDate).isBefore(dayjs(), 'day')
          ? dayjs(dateRange.maxDate).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD')
      }

  const [form, setForm] = useState(initialForm)

  useEffect(() => {
    setTimeout(() => {
      if (editLabel && editLabel.data) {
        setForm({
          title: editLabel.data.title,
          description: editLabel.data.description,
          cpi_configuration_id: cpiConfId,
          visibility: editLabel.data.is_public ? 'public' : 'private',
          date: editLabel.data.date ? dayjs(editLabel.data.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
        })
      }
    }, 300)
  }, [editLabel])

  const onInputChange = (e: any, type?: string) => {
    if (type === 'date') {
      setForm((prevState) => ({
        ...prevState,
        [e.name]: dayjs(e.value).format('YYYY-MM-DD')
      }))

      return
    }

    setForm((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }))
  }

  const getTitleError = () => {
    if (form.title.length > 20) {
      return 'Title must be 20 characters or less'
    }
    return ''
  }

  const getDescriptionError = () => {
    if (form.description.length > 50) {
      return 'Description must be 50 characters or less'
    }
    return ''
  }

  const isValid = () => {
    return form.title.length && form.title.length <= 20 && form.description.length && form.description.length <= 50
  }

  const onSubmit = async () => {
    try {
      setLoading(true)
      if (!form.title) form.title = 'Custom Label'

      const utcDate = getUtcDateAndTime(dayjs(form.date).format('YYYY-MM-DD'))
      const d = utcDate?.isoFormattedDate || ''

      const { visibility, ...formWithoutVisibility } = form
      const payload = {
        ...formWithoutVisibility,
        date: d || dayjs(form.date).format('YYYY-MM-DD'),
        is_public: visibility === 'public'
      }

      if (editLabel && Object.keys(editLabel).length > 0) {
        if (mode === 'edit') {
          await axios.put(`/api/v3/labels/cpis/custom/${editLabel.id}`, payload)
        } else if (mode === 'delete') {
          await axios.delete(`/api/v3/labels/cpis/custom/${editLabel.id}`)

          if (deleteLabelCallback) {
            await deleteLabelCallback(editLabel.id)
          }
        }
      } else {
        await axios.post(`/api/v3/labels/cpis/custom/${cpiName}`, payload)
      }

      await updateLabels()
      // await updateLabels(undefined, true)
      updateMode(customDate.start && redirectToMode ? redirectToMode : 'show-0')
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setForm(initialForm)
    returnToZero()
  }

  const createdFromOtherUser = useMemo(
    () => (editLabel && editLabel.username ? authUser?.name !== editLabel.username : false),
    [authUser, editLabel]
  )

  return (
    <LabelsShowModeWrapper>
      <Formik initialValues={form} onSubmit={onSubmit}>
        {({ isSubmitting }) => (
          <Form autoComplete="off" className="create-label-form">
            <CreateLabelsInnerForm>
              <Box className="show-mode-content">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography className="form-control-label">Label For Date</Typography>
                  <SingleDatePicker
                    value={dayjs(form.date)}
                    onChange={(e) => onInputChange({ name: 'date', value: e }, 'date')}
                    label=""
                  />
                </Box>

                <TextFieldCustomLabel
                  name="title"
                  fullWidth
                  label="Title"
                  variant="outlined"
                  placeholder="Enter a title..."
                  value={form.title}
                  showAsterisk={false}
                  onChange={(e) => onInputChange(e)}
                  counterLimit={20}
                  helperText={getTitleError()}
                />

                <ControlledTextArea
                  name="description"
                  fullWidth
                  label="Description"
                  placeholder="Enter a description..."
                  variant="outlined"
                  multiline
                  minRows={4}
                  value={form.description}
                  showAsterisk={false}
                  onChange={(e) => onInputChange(e)}
                  counterLimit={50}
                  helperText={getDescriptionError()}
                />

                <FormikRadioGroup
                  name="visibility"
                  options={visibilityOptions}
                  label="Label Visibility"
                  value={form.visibility}
                  onChange={(e) => onInputChange(e)}
                  formControlSx={{ flexGrow: 1 }}
                  disabled={!!(editLabel && editLabel.data)}
                />
              </Box>

              <CreateLabelWrapper>
                <Button themevariant="secondary" type="button" className="create-button" onClick={handleCancel}>
                  Cancel
                </Button>
                {createdFromOtherUser ? (
                  <OnyxiaTooltip
                    placement="top"
                    title="You don’t have permission to edit this label. Only the creator can modify it."
                    sx={{ width: '300px !important' }}
                  >
                    <Button
                      themevariant="hollow"
                      type="submit"
                      className={`create-button  ${!isValid() || isSubmitting ? 'disabled-btn' : ''}`}
                      sx={disabledStyles.secondary}
                    >
                      {mode === 'edit' ? 'Save' : mode === 'delete' ? 'Delete' : 'Create'}
                    </Button>
                  </OnyxiaTooltip>
                ) : (
                  <Button
                    themevariant="hollow"
                    type="submit"
                    className={`create-button  ${!isValid() || isSubmitting ? 'disabled-btn' : ''}`}
                    disabled={!isValid() || isSubmitting}
                  >
                    {mode === 'edit' ? 'Save' : mode === 'delete' ? 'Delete' : 'Create'}
                  </Button>
                )}
              </CreateLabelWrapper>
            </CreateLabelsInnerForm>
          </Form>
        )}
      </Formik>
    </LabelsShowModeWrapper>
  )
}

export default CreateLabels
